import React, { useEffect, useRef,useState } from 'react'
import { useHistory } from 'react-router-dom'

import cx from 'classnames'

import { driftErrorStateBuilder, driftLoadingStateBuilder } from 'components/States'
import { InfiniteScrollTable } from 'components/TableView'
import { FilterPage } from 'context/filtersContext'
import { PagedResponseData } from 'hooks/paginationAndApiUtils'
import { AccountActivitySortValue,Insight } from 'modules/accountActivity/domain'
import { segmentTrack,TrackType } from 'utils/tracking'

import { useEmptyComponent } from '../../../hooks/useEmptyComponent'

import GroupedActivitiesFeedRow from './GroupedActivitiesFeedRow'

import styles from './styles.module.css'

type Id = number
type Index = number

const CONTACTS_PER_CHUNK = 3
const ACTIVITIES_PER_CHUNK = 5
export const CLOSED_ROW_HEIGHT = 57

interface GroupedActivitiesFeedProps {
  insightsFeed: PagedResponseData<Insight>
  filterPage: FilterPage
  selectedSort: AccountActivitySortValue
  onSortByChange: (value?: any) => void
  descending: boolean
  onSortDirChange: (value?: any) => void
  initialScrollOffset?: number | undefined
  onScrollOffsetChange?: ((offset: number) => void) | undefined
  onInsightRowExpanded?: () => void
}

const cache: {
  [key: string]: {
    isExpandedAt: Map<Id, boolean>
    rowHeightAt: Map<Index, number>
    maxContactsAt: Map<Id, number>
    maxActivitiesAt: Map<Id, Map<Index, number>>
  }
} = {
  [FilterPage.ACTIVITIES]: {
    isExpandedAt: new Map(),
    rowHeightAt: new Map(),
    maxContactsAt: new Map(),
    maxActivitiesAt: new Map()
  },
  [FilterPage.ACCOUNT_DETAILS]: {
    isExpandedAt: new Map(),
    rowHeightAt: new Map(),
    maxContactsAt: new Map(),
    maxActivitiesAt: new Map()
  }
}

const GroupedActivitiesFeed: React.FC<GroupedActivitiesFeedProps> = ({
  insightsFeed,
  filterPage,
  selectedSort,
  onSortByChange,
  descending,
  onSortDirChange,
  initialScrollOffset,
  onScrollOffsetChange,
  onInsightRowExpanded
}) => {
  const [isExpandedAt, setIsExpandedAt] = useState<Map<Id, boolean>>(cache[filterPage].isExpandedAt)
  const [rowHeightAt, setRowHeightAt] = useState<Map<Index, number>>(cache[filterPage].rowHeightAt)
  const [maxContactsAt, setMaxContactsAt] = useState<Map<Id, number>>(
    cache[filterPage].maxContactsAt
  )
  const [maxActivitiesAt, setMaxActivitiesAt] = useState<Map<Id, Map<Index, number>>>(
    cache[filterPage].maxActivitiesAt
  )

  const history = useHistory()

  useEffect(() => {
    const getOpenInsightRow = () => {
      const queryParams = new URLSearchParams(window.location.search)

      if (queryParams.has('openInsight')) {
        const openInsightRow = queryParams.get('openInsight')
        queryParams.delete('openInsight')
        history.replace({ search: queryParams.toString() })

        return openInsightRow
      }

      return null
    }

    const openInsightRow = getOpenInsightRow()

    if (openInsightRow && !isExpandedAt.get(parseInt(openInsightRow))) {
      isExpandedAt.set(parseInt(openInsightRow), true)
    }

    return () => {
      cache[filterPage].isExpandedAt = isExpandedAt
      cache[filterPage].rowHeightAt = rowHeightAt
      cache[filterPage].maxContactsAt = maxContactsAt
      cache[filterPage].maxActivitiesAt = maxActivitiesAt
    }
  }, [isExpandedAt, rowHeightAt, maxContactsAt, maxActivitiesAt, filterPage, history])

  const tableRef = useRef<any>(null)

  const toggleExpanded = (insight: Insight, index: Index) => {
    const isInsightAlreadyExpanded = isExpandedAt.get(insight.id)
    segmentTrack(TrackType.INSIGHT_FEED_ROW_TOGGLED, {
      insight_id: insight.id,
      accountId: insight.accountId,
      companyName: insight.companyName
    })
    if (!isInsightAlreadyExpanded && onInsightRowExpanded) {
      onInsightRowExpanded()
    }
    setIsExpandedAt((m: Map<Id, boolean>) => new Map(m).set(insight.id, !isInsightAlreadyExpanded))
    if (tableRef.current) {
      tableRef.current.resetAfterIndex(index, true)
    }
  }

  const handleChangeRowHeight = (index: Index, height: number) => {
    if (rowHeightAt.get(index) !== height) {
      setRowHeightAt((m: Map<Index, number>) => new Map(m).set(index, height))
      if (tableRef.current) {
        tableRef.current.resetAfterIndex(index)
      }
    }
  }

  const handleClickShowMoreContacts = (insight: Insight) => {
    segmentTrack(TrackType.INSIGHT_FEED_SHOW_MORE_CONTACTS, {
      insight_id: insight.id
    })
    setMaxContactsAt((contactsAt: Map<Id, number>) =>
      new Map(contactsAt).set(
        insight.id,
        (contactsAt.get(insight.id) || CONTACTS_PER_CHUNK) + CONTACTS_PER_CHUNK
      )
    )
  }

  const handleClickShowMoreActivities = (insight: Insight, contactIndex: Index) => {
    segmentTrack(TrackType.INSIGHT_FEED_SHOW_MORE_ACTIVITIES, {
      insight_id: insight.id
    })
    setMaxActivitiesAt((activitiesAtContactsAt: Map<Index, Map<Index, number>>) => {
      const activitiesAt = activitiesAtContactsAt.get(insight.id) || new Map()
      const nextValue =
        (activitiesAt.get(contactIndex) || ACTIVITIES_PER_CHUNK) + ACTIVITIES_PER_CHUNK

      return new Map(activitiesAtContactsAt).set(
        insight.id,
        new Map(activitiesAt).set(contactIndex, nextValue)
      )
    })
  }

  const {
    data,
    error,
    size,
    setSize,
    isLoadingMore,
    isLoadingInitialData,
    hasLoadedInitialData,
    isReachingEnd
  } = insightsFeed

  const totalHeight = data.reduce((sum: number, insight: Insight, index: number) => {
    const height = isExpandedAt.get(insight.id)
      ? (rowHeightAt.get(index) || 0) + 81
      : CLOSED_ROW_HEIGHT

    return sum + height
  }, 0)

  const EmptyComponent = useEmptyComponent()

  return (
    <div className={cx(styles.tableContainer, styles.tableContainerSavedViews)}>
      <div
        className={cx(styles.subTableContainer, {
          [styles.subTableContainerSavedViews]: filterPage !== FilterPage.ACCOUNT_DETAILS
        })}
        style={{
          height:
            filterPage === FilterPage.ACCOUNT_DETAILS
              ? 'max(100%, 344px)'
              : data && data.length > 0
              ? `calc(min(100% - 32px, ${totalHeight}px) + 33px)`
              : '100%'
        }}>
        <InfiniteScrollTable
          forwardedRef={tableRef}
          rowHeight={index => (rowHeightAt.get(index) || -24) + 81}
          rowClassName={styles.row}
          columns={[
            { label: 'Contact(s)', sortable: false, margin: '14px' },
            {
              label: '# of activities',
              margin: '8px',
              sortable: true,
              sortValue: AccountActivitySortValue.NUM_ACTIVITIES
            },
            {
              label: 'Most recent activity',
              margin: '8px',
              sortable: true,
              sortValue: AccountActivitySortValue.MOST_RECENT
            }
          ]}
          resultSize={100_000} // setting arbitrarily high allows scrolling to work before count has loaded
          data={data}
          loadMore={() => {
            !isReachingEnd && !isLoadingMore && !isLoadingInitialData && setSize((size ?? 0) + 1)
          }}
          cellRenderers={[
            (insight: any, index: Index) => (
              <GroupedActivitiesFeedRow
                insight={insight}
                filterPage={filterPage}
                expanded={!!isExpandedAt.get(insight.id)}
                onChangeRowHeight={(height: number) => handleChangeRowHeight(index, height)}
                maxContactToShow={maxContactsAt.get(insight.id) || CONTACTS_PER_CHUNK}
                onClickShowMoreContacts={() => handleClickShowMoreContacts(insight)}
                getMaxActivitiesToShow={(contactIndex: Index) => {
                  const m = maxActivitiesAt.get(insight.id)
                  return m ? m.get(contactIndex) || ACTIVITIES_PER_CHUNK : ACTIVITIES_PER_CHUNK
                }}
                onClickShowMoreActivities={(contactIndex: Index) => {
                  handleClickShowMoreActivities(insight, contactIndex)
                }}
              />
            )
          ]}
          onRowClick={toggleExpanded}
          error={error}
          errorComponent={driftErrorStateBuilder('your account activity')}
          loadingComponent={driftLoadingStateBuilder('Loading activity')}
          emptyComponent={EmptyComponent}
          isLoadingInitialData={isLoadingInitialData}
          initialDataLoaded={hasLoadedInitialData}
          selectedSort={selectedSort}
          onSortByChange={onSortByChange}
          descending={descending}
          onSortDirChange={onSortDirChange}
          initialScrollOffset={initialScrollOffset}
          onScrollOffsetChange={onScrollOffsetChange}
        />
      </div>
    </div>
  )
}

export default GroupedActivitiesFeed
